import { useEffect, useState } from "react";
import { AnswerOption } from "../../components/AnswerOption";
import { Button } from "../../components/Button";
import { clock } from "../../lib/format";
import type { Question } from "../../lib/practice";
import { QuestionText } from "../../components/QuestionText";

interface MockRunnerScreenProps {
  questions: Question[];
  /**
   * Seed only — the timer owns the countdown from here.
   *
   * Named `initialSeconds` because it is read once: a prop that silently
   * stopped mattering after mount would be a trap for whoever wires the resume
   * flow. Resuming a paused mock remounts with a new seed.
   */
  initialSeconds: number;
  onSubmit: (answers: Record<string, string>) => void;
  onOpenPalette?: (() => void) | undefined;
}

/**
 * Screen 11 — the mock runner is deliberately unlike practice.
 *
 * No per-question feedback, because the point is to rehearse the hall: a
 * student who learns after each question is not practising exam conditions,
 * and their mock score stops predicting anything.
 *
 * Theory questions (no options) get a textarea instead of answer chips — a
 * student must be able to write their answer just like in the real exam.
 *
 * The timer turns red under five minutes — the one moment where colour is
 * doing real work, so it is paired with the count itself rather than replacing
 * it.
 */
const LOW_TIME_SECONDS = 5 * 60;

export function MockRunnerScreen({
  questions,
  initialSeconds,
  onSubmit,
  onOpenPalette,
}: MockRunnerScreenProps) {
  const [index, setIndex] = useState(0);
  const [answers, setAnswers] = useState<Record<string, string>>({});
  const [left, setLeft] = useState(initialSeconds);

  useEffect(() => {
    if (left <= 0) return;
    const timer = setTimeout(() => setLeft((seconds) => seconds - 1), 1000);
    return () => clearTimeout(timer);
  }, [left]);

  const question = questions[index];
  if (!question) return null;

  const answeredCount = Object.keys(answers).length;
  const lowTime = left <= LOW_TIME_SECONDS;
  const isTheory = question.options.length === 0;

  return (
    <div className="app-column px-5 pb-6 pt-4">
      {/* Locked header: no back chevron, no tab bar. Leaving is a decision,
          not a slip. */}
      <header className="flex items-center justify-between gap-3 border-b border-border pb-3">
        <span className="text-[13px] font-semibold">{question.subject}</span>
        <span
          className={`tabular text-[17px] font-bold ${lowTime ? "text-negative" : "text-text"}`}
          role="timer"
          aria-label={`${clock(left)} remaining`}
        >
          {clock(left)}
        </span>
        <button
          type="button"
          onClick={onOpenPalette}
          aria-label="Show all questions"
          className="tabular rounded-[var(--radius-chip)] border border-border bg-surface px-2.5 py-1 text-[12px] text-text-muted"
        >
          {answeredCount}/{questions.length}
        </button>
      </header>

      <p className="tabular m-0 pt-4 text-[12px] text-text-muted">
        Question {index + 1} of {questions.length}
        {isTheory ? (
          <span className="ml-2 rounded-[var(--radius-chip)] bg-surface-high px-2 py-0.5 text-[10px] font-semibold uppercase">
            Theory
          </span>
        ) : null}
      </p>
      <QuestionText as="h1" className="m-0 pt-2 text-[17px] font-semibold leading-[1.45]">
        {question.stem}
      </QuestionText>

      {isTheory ? (
        /* Theory: a textarea for written answers, just like the real exam. */
        <div className="pt-5">
          <label
            htmlFor={`theory-${question.id}`}
            className="text-[11px] font-semibold uppercase tracking-[0.1em] text-text-muted"
          >
            Your answer
          </label>
          <textarea
            id={`theory-${question.id}`}
            rows={8}
            value={answers[question.id] ?? ""}
            onChange={(event) =>
              setAnswers((current) => ({ ...current, [question.id]: event.target.value }))
            }
            placeholder="Write your answer as you would in the exam…"
            className="mt-2 w-full resize-y rounded-[var(--radius-control)] border border-border-strong bg-surface p-3.5 text-[14.5px] leading-[1.55] text-text outline-none placeholder:text-text-muted focus:border-accent"
          />
        </div>
      ) : (
        /* Multiple choice: answer chips, no feedback. */
        <ul className="m-0 flex list-none flex-col gap-2.5 p-0 pt-5">
          {question.options.map((option, optionIndex) => (
            <li key={option.id}>
              <AnswerOption
                letter={String.fromCharCode(65 + optionIndex)}
                text={option.text}
                // "selected", never "correct"/"wrong" — the whole point.
                state={answers[question.id] === option.id ? "selected" : "idle"}
                onClick={() => setAnswers((current) => ({ ...current, [question.id]: option.id }))}
              />
            </li>
          ))}
        </ul>
      )}

      <p className="m-0 pt-4 text-[12px] text-text-muted">
        No feedback till you submit — just like the hall.
      </p>

      <div className="mt-auto flex gap-2 pt-8">
        <Button
          variant="secondary"
          className="text-[13px]"
          disabled={index === 0}
          onClick={() => setIndex((current) => current - 1)}
        >
          Previous
        </Button>
        <Button
          variant="ghost"
          className="text-[13px]"
          onClick={() => setIndex((current) => Math.min(current + 1, questions.length - 1))}
        >
          Skip
        </Button>
        {index === questions.length - 1 ? (
          <Button className="text-[13px]" onClick={() => onSubmit(answers)}>
            Submit
          </Button>
        ) : (
          <Button className="text-[13px]" onClick={() => setIndex((current) => current + 1)}>
            Next
          </Button>
        )}
      </div>
    </div>
  );
}
